import { Injectable } from '@angular/core';
import { Question, SchoolQuestions } from '../_models/question';
import { Firestore, doc, docData, setDoc, getDoc, collection, addDoc, DocumentReference } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { BehaviorSubject, Subscription, firstValueFrom } from 'rxjs';
import { tap } from 'rxjs/operators';

import { find, differenceBy } from 'lodash-es';
import { convertTimestamps, convertTimestampsPipe } from 'app/_pipes/convert-firestore-timestamp.pipe';
import { AuthService } from './auth.service';

export interface PropagateQuestionUpdatesResult {
  schoolsScanned: number;
  documentsUpdated: number;
  questionsReplaced: number;
}

export interface QuestionSchoolIndexDoc {
  byQuestion?: Record<string, string[]>;
  counts?: Record<string, number>;
  schoolCount?: number;
  builtAt?: Date;
  builtBy?: string;
  sharded?: boolean;
}

/**
 * Yes/no gateway checklist inputs (+ calculated dependency status). Keep under DS
 * for super-admin review. Birth year / marital status are SF form fields and remap
 * with other former Starting (ST) questions. Keep in sync with npc-playground
 * dependency-gateway GATEWAY_YES_NO_IDS.
 */
export const DEPENDENCY_SECTION_IDS = [
  'student.hasDependentsIndicator',
  'student.veteranIndicator',
  'student.orphanIndicator',
  'student.emancipatedMinor',
  'student.homelessIndicator',
  'student.dependencyStatus',
] as const;

/** Identity / meta fields collected outside school-managed form sections — leave as ST. */
const UNMAPPED_START_IDS = new Set([
  'student.firstName',
  'student.completingCalculator',
]);

/** Start section is gone — move ST leftovers into SF, or DS for gateway checklist inputs. */
export function normalizeSchoolQuestionSections(questions: Question[]): void {
  const dependencyIds = new Set<string>(DEPENDENCY_SECTION_IDS);
  for (const question of questions) {
    if (question.section_id !== 'ST' || UNMAPPED_START_IDS.has(question.id)) {
      continue;
    }
    question.section_id = dependencyIds.has(question.id) ? 'DS' : 'SF';
  }
}

// In 2023, we started to move from the collection questions_draft to calcConfig/schoolId/questions/aidYear.
// The aidYear questions SHOULD be the same as the questions_draft, but we need to make sure.
// TODO: Then move this application and the student version to use the calcConfig/schoolId/questions/aidYear.
@Injectable()
export class QuestionsService {

  draftQuestions = new BehaviorSubject<Question[]>([]);
  draftLastUpdated = new BehaviorSubject<Date | null>(null); // Tracked using the aidYearQuestionsSubscription
  numericDraftQuestions = new BehaviorSubject<Question[]>([]); // Can be used as a selector for numeric questions
  allQuestions = new BehaviorSubject<Question[]>([]);
  unusedQuestions = new BehaviorSubject<Question[]>([]); // Used to populate the Add Question dropdown



  // Track the school and aid year of the last time we loaded questions
  loadedSchoolId = '';
  loadedAidYear: number | null = null;
  allQuestionsLoadedAidYear: number | null = null;

  // Track subscriptions so we can unsubscribe
  draftSubscription?: Subscription;
  aidYearQuestionsSubscription?: Subscription;
  allQuestionsSubscription?: Subscription;


  questionSchoolIndex = new BehaviorSubject<QuestionSchoolIndexDoc | null>(null);
  private questionSchoolIndexSubscription?: Subscription;

  constructor(
    private firestore: Firestore,
    private auth: AuthService,
    private functions: Functions,
  ) { }

  // We need to load all questions for the aid year and the draft questions for the school
  public loadQuestions(schoolId: string, aidYear: number) {
    // Load master list of all questions if not already loaded for this aid year
    if (aidYear !== this.allQuestionsLoadedAidYear) {
      this.allQuestions.next([]);
      this.loadAllQuestions(aidYear);
    }

    // If the schoolId and aidYear are the same as the last time we loaded questions, don't do it again
    if (schoolId === this.loadedSchoolId && aidYear === this.loadedAidYear) {
      return;
    }

    this.draftQuestions.next([]);
    this.numericDraftQuestions.next([]);
    this.loadedSchoolId = '';
    this.loadedAidYear = null;
    if (this.draftSubscription) { this.draftSubscription.unsubscribe(); }

    const draftDocRef = doc(this.firestore, 'questions_draft', schoolId);
    this.draftSubscription = docData(draftDocRef).pipe(
      convertTimestampsPipe(),
      tap(res => {
        // Copied schools (the old default-school setup) have calc rules and no questions_draft.
        // That is an empty list, not a failed load. Otherwise Apply minimum questions
        // thinks the master list never arrived because loadedAidYear stays unset.
        const questions: Question[] = Array.isArray(res?.questions) ? res.questions : [];
        if (res) {
          console.log(schoolId, this.draftQuestions.value.length === 0 ? 'loaded' : 're-loaded', questions.length, 'questions for aid year', aidYear);
        } else {
          console.log(schoolId, 'has no questions_draft; using an empty list for aid year', aidYear);
        }
        normalizeSchoolQuestionSections(questions);
        this.draftQuestions.next(questions);
        this.numericDraftQuestions.next(
          questions.filter((q: Question) => q.format === 'Number' || q.format === 'Integer' || q.format === 'NumberAllowNegative')
        );
        this.addDependencyQuestion();
        this.populateUnusedQuestions();  // This is performed here AND when loading all questions to make sure it is up to date
        this.loadedSchoolId = schoolId;
        this.loadedAidYear = aidYear;
      })
    ).subscribe();

    if (this.aidYearQuestionsSubscription) { this.aidYearQuestionsSubscription.unsubscribe(); }
    const aidYearDocRef = doc(this.firestore, `calcConfig/${schoolId}/questions/${aidYear}`) as DocumentReference<SchoolQuestions>;
    this.aidYearQuestionsSubscription =     // Subscribe to questions document
      docData(aidYearDocRef).pipe(
        convertTimestampsPipe(),
        tap(res => {
          if (res?.lastUpdated) {
            console.log(res?.lastUpdated, 'last updated');
            this.draftLastUpdated.next(res.lastUpdated as Date);
          } else {
            this.draftLastUpdated.next(null);
          }
        })
      ).subscribe();
  }

  populateUnusedQuestions() {
    // const extraList = differenceBy(this.allQuestions.value, this.draftQuestions.value, 'id').filter(q => q.triggerQuestionId === undefined);
    // Show triggerred questions now and then don't allow user in component to add them directly (need to use triggering question)
    // TODO: Flag triggered questions as not addable if their triggering question is in this list
    const extraList = differenceBy(this.allQuestions.value, this.draftQuestions.value, 'id')
      .filter((q: Question) => !q.dateDeleted); // Filter out deleted questions
    this.unusedQuestions.next(extraList);
  }

  async saveQuestions(schoolId: string, questions: Question[], aidYear: number | null) {
    if (!aidYear) {
      alert('Aid year is null, cannot save questions');
      return;
    }
    const user = await this.auth.currentUser();
    normalizeSchoolQuestionSections(questions);
    const schoolQuestions: SchoolQuestions = {
      schoolId: schoolId,
      aidYear: aidYear,
      questions: questions,
      lastUpdated: new Date(Date.now()),
      lastUpdatedBy: user?.uid ?? ''
    };
    // This updates the aid year directly.  If we switch the student app to use this then we are automatically publishing the questions.
    // We would need to add a live version of the questions in this collection that the student app would use.
    // or keep the questions collection for the student app and the current publishquestions method.
    const aidYearDocRef = doc(this.firestore, `calcConfig/${schoolId}/questions/${aidYear}`);
    await setDoc(aidYearDocRef, schoolQuestions);
    const draftDocRef = doc(this.firestore, 'questions_draft', schoolId);
    await setDoc(draftDocRef, { questions: questions });
    console.log('Updated ', questions.length, ' Questions');
  }

  // Called from Inspector Component when Publishing Rules
  public async publishQuestions(schoolId: string): Promise<string> {
    const questions = this.draftQuestions.value;
    try {
      const questionsDocRef = doc(this.firestore, 'questions', schoolId);
      await setDoc(questionsDocRef, { questions: questions });
    } catch (err) {
      return 'Contact Support - Questions not published: ' + err;
    }
    return 'Questions successfully published';
  }
  // Called from Inspector Component when Resetting Rules
  public async resetQuestions(schoolId: string) {
    const docRef = doc(this.firestore, 'questions', schoolId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      // Make a backup copy of the draft questions (should have the lastest date updated in hte backup folder )
      const draftQuestions = this.draftQuestions.value;
      const user = await this.auth.currentUser();
      const schoolQuestions: SchoolQuestions = {
        schoolId: schoolId,
        aidYear: this.loadedAidYear ?? 0,
        questions: draftQuestions,
        lastUpdated: new Date(Date.now()),
        lastUpdatedBy: user?.uid ?? ''
      };
      const backupCollectionRef = collection(this.firestore, `calcConfig_backup/${schoolId}/questions`);
      await addDoc(backupCollectionRef, schoolQuestions);

      const questions = docSnap.data();
      const draftDocRef = doc(this.firestore, 'questions_draft', schoolId);
      await setDoc(draftDocRef, questions);
    }
  }


  addDependencyQuestion() {
    const dq = find(this.draftQuestions.value, { 'id': 'student.dependencyStatus' });
    if (!dq) {
      const q: Question = {
        section_id: 'DS',
        format: 'Code',
        id: 'student.dependencyStatus',
        label: 'Calculated Dependency Status',
        ordinal: 1,
        choices: [{ label: 'Dependent', value: 'Dependent', ord: 1 }, { label: 'Independent', value: 'Independent', ord: 2 }],
        help: '',
        value: '',
        hidden: true,
        indent: false,
        required: false,
        dependent: true,
        independent: true,
        methodRequired: true
      };
      const questions = this.draftQuestions.value;
      questions.push(q);
      this.draftQuestions.next(questions);
    }
  }



  loadQuestionSchoolIndex(aidYear: number | null): void {
    this.questionSchoolIndexSubscription?.unsubscribe();
    this.questionSchoolIndex.next(null);
    if (!aidYear) {
      return;
    }
    const docRef = doc(this.firestore, 'questionSchoolIndex', aidYear.toString());
    this.questionSchoolIndexSubscription = docData(docRef).pipe(
      convertTimestampsPipe(),
      tap(res => {
        this.questionSchoolIndex.next((res as QuestionSchoolIndexDoc) ?? null);
      })
    ).subscribe();
  }

  async propagateQuestionUpdates(aidYear: number | null, questionIds: string[]): Promise<PropagateQuestionUpdatesResult> {
    if (!aidYear) {
      throw new Error('Aid year is required to update school lists');
    }
    if (questionIds.length === 0) {
      throw new Error('A question id is required to update school lists');
    }
    const callable = httpsCallable<{ aidYear: number; questionIds: string[] }, PropagateQuestionUpdatesResult>(
      this.functions,
      'propagateQuestionUpdates'
    );
    const result = await callable({ aidYear, questionIds });
    return result.data;
  }

  async rebuildQuestionSchoolIndex(aidYear: number | null): Promise<{ schoolCount: number; questionCount: number; sharded: boolean }> {
    if (!aidYear) {
      throw new Error('Aid year is required to rebuild the school index');
    }
    const callable = httpsCallable<{ aidYear: number }, { schoolCount: number; questionCount: number; sharded: boolean }>(
      this.functions,
      'rebuildQuestionSchoolIndex'
    );
    const result = await callable({ aidYear });
    return result.data;
  }

  questionCountFor(questionId: string): number | null {
    const index = this.questionSchoolIndex.value;
    if (!index) {
      return null;
    }
    if (index.counts && questionId in index.counts) {
      return index.counts[questionId];
    }
    return index.byQuestion?.[questionId]?.length ?? 0;
  }

  async schoolIdsForQuestion(questionId: string): Promise<string[]> {
    const index = this.questionSchoolIndex.value;
    if (!index) {
      return [];
    }
    if (index.byQuestion?.[questionId]) {
      return [...index.byQuestion[questionId]];
    }
    if (!index.sharded) {
      return [];
    }
    const childRef = doc(this.firestore, 'questionSchoolIndex', String(this.allQuestionsLoadedAidYear), 'byQuestion', encodeURIComponent(questionId));
    const childSnap = await getDoc(childRef);
    const schoolIds = childSnap.data()?.['schoolIds'];
    return Array.isArray(schoolIds) ? schoolIds.filter((id): id is string => typeof id === 'string') : [];
  }

  getQuestion(id: string): Question {
    return this.draftQuestions.value.filter(obj => obj.id === id)[0];
  }

  async getAllQuestionsForAidYear(aidYear: number): Promise<Question[]> {
    const docRef = doc(this.firestore, 'allQuestions', aidYear.toString());
    const questions = await firstValueFrom(docData(docRef));
    return questions?.questions || [];
  }


  async loadAllQuestions(aidYear: number) {
    if (this.allQuestionsSubscription) { this.allQuestionsSubscription.unsubscribe(); }

    const docRef = doc(this.firestore, 'allQuestions', aidYear.toString());
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const res = convertTimestamps(docSnap.data());
      console.log('All', res.questions.length, 'Questions loaded for aid year', aidYear);
      normalizeSchoolQuestionSections(res.questions);
      this.allQuestionsLoadedAidYear = aidYear;
      this.allQuestions.next(res.questions);
      this.populateUnusedQuestions();
    } else {
      alert('The master list of all questions for aid year ' + aidYear + ' not found');
    }
  }

  async saveAllQuestions(questions: Question[], aidYear: number | null) {
    if (!aidYear) {
      alert('Aid year is null, cannot save questions');
      return;
    }
    const cleanQuestions = questions.filter(q => q.id); // Not needed anymore, but doesn't hurt
    const docRef = doc(this.firestore, 'allQuestions/' + aidYear);
    await setDoc(docRef, { questions: cleanQuestions });
  }


  public flagDraftQuestions(schoolId: string, institutionalMethod: boolean) {

    if (this.loadedSchoolId !== schoolId) {
      const accept = confirm('No questions exist, continue with adding initial questions?');
      if (!accept) {
        return;
      }
    }

    const im = institutionalMethod;

    const allQuestions: Question[] = this.allQuestions.value;
    const questions = this.draftQuestions.value;
    questions.forEach(q => {
      const newQuestion = find(allQuestions, { 'id': q.id });
      if (newQuestion) {

        const methodRequired = im ? newQuestion.imRequired || false : newQuestion.fmRequired || false;
        if (methodRequired !== q.methodRequired) {
          console.log('Changing question to Method Required =', methodRequired, 'for question', q.id);
          q.methodRequired = methodRequired;
        }
      } else {
        console.log('Quetion missing from all questions', q.id);
      }
    });

    // Find any questions that are required in the other methodology that were not there and add them
    const extraList = differenceBy(allQuestions, this.draftQuestions.value, 'id');
    const addQuestions = extraList.filter((q: Question) => im ? q.imRequired === true : q.fmRequired === true);
    addQuestions.forEach(q => {
      console.log('Adding required Question', q.id);
      q.methodRequired = true;
      questions.push(q);
    });

    this.saveQuestions(schoolId, questions, this.loadedAidYear);
  }
}